import { IAlertProps } from "@components/alert/Alert.types";
import Dialog from "@components/dialog";
import Field from "@components/inputs/field";
import FieldsWrapper from "@components/inputs/field/FieldsWrapper";
import Input, { IInputOnBlurEvent, IInputOnChangeEvent } from "@components/inputs/input";
import { IPrPayslipEntity } from "@odata/GeneratedEntityTypes";
import { sendPayslipsByEmail } from "@pages/payroll/payslip/Payslip.utils";
import React from "react";
import { WithTranslation, withTranslation } from "react-i18next";

import { EMAIL_REGEX } from "../../../constants";
import { BasicInputSizes } from "../../../enums";
import { IValidationError } from "../../../model/Validator.types";
import { FormStorage } from "../../../views/formView/FormStorage";
import ConfirmationButtons from "../../../views/table/ConfirmationButtons";

interface IProps {
    storage: FormStorage;
    onClose: (alert?: IAlertProps) => void;
}

interface IState {
    error?: IValidationError;
    email?: string;
    isBusy?: boolean;
}

class SendPayslipByEmailDialog extends React.Component<IProps & WithTranslation, IState> {

    constructor(props: IProps & WithTranslation) {
        super(props);

        const payslip = props.storage.data.entity as IPrPayslipEntity;
        this.state = {
            error: null,
            email: payslip.Employee?.Email,
            isBusy: false
        };
    }

    handleClose = (alert?: IAlertProps): void => {
        this.props.onClose(alert);
    };

    handleChange = (e: IInputOnChangeEvent<string>): void => {
        this.setState({
            email: e.value,
            error: null
        });
    };

    handleBlur = (e: IInputOnBlurEvent): void => {
        if (e.wasChanged) {
            const email = this.state.email?.trim();
            const error = this.validateEmailDialogValue(email);
            this.setState({ email, error });
        }
    }

    validateEmailDialogValue = (email: string): IValidationError => {
        if (!email) {
            return { message: this.props.t("Common:Validation.Required") };
        }

        if (!EMAIL_REGEX.test(email)) {
            return { message: this.props.t("Common:Validation.WrongEmail") };
        }

        return null;
    };

    handleSendPayslipEmail = async (): Promise<void> => {
        const { storage } = this.props;
        const email = this.state.email.trim();
        const emailError = this.validateEmailDialogValue(email);

        if (emailError) {
            this.setState({ error: emailError });
            return;
        }

        this.setState({ isBusy: true, error: null });
        const Id = storage.data.bindingContext.getKey() as number;
        const alert = await sendPayslipsByEmail([Id], email);
        this.setState({ isBusy: false });
        this.handleClose(alert);
    };

    render(): React.ReactElement {
        const { email, error, isBusy } = this.state;

        return (
            <Dialog
                title={this.props.t("Payslip:SendPayslipDialog.Title")}
                onConfirm={null}
                onClose={this.handleClose}
                busy={isBusy}
                footer={
                    <ConfirmationButtons
                        confirmText={this.props.t("Payslip:SendPayslipDialog.Send")}
                        onConfirm={this.handleSendPayslipEmail}
                        onCancel={this.handleClose}
                        isDisabled={isBusy}
                        useWrapper={false}
                    />
                }
            >
                <FieldsWrapper>
                    <Field label={this.props.t("Payslip:SendPayslipDialog.Email")} isRequired>
                        <Input
                            value={email ?? ""}
                            onChange={this.handleChange}
                            onBlur={this.handleBlur}
                            error={error}
                            width={BasicInputSizes.XL}
                        />
                    </Field>
                </FieldsWrapper>
            </Dialog>
        );
    }
}

export default withTranslation([
    "Payslip", "Common"
])(SendPayslipByEmailDialog);